import { z } from 'zod';
import type { Permission } from './permissions';

/**
 * The settings catalog: the index of every configurable area of the product.
 *
 * This is the same extension model as the component catalog in
 * `components.ts` -- a definition declared as data here, an editor keyed by the
 * same identifier in the frontend registry. Adding a settings area means one
 * entry in each, and no change to the settings page, its navigation, or any
 * other area.
 *
 * Three decisions are worth stating, because the obvious alternatives are all
 * slightly wrong:
 *
 * **The catalog indexes settings; it does not store them.** A generic
 * key/value settings table would make new sections free on the backend, and it
 * would cost the things that make settings trustworthy: `ScheduleCalendar` has
 * its own table, its own `version` for optimistic concurrency, and validation
 * that holidays are real calendar dates. Collapsing that into an untyped blob
 * trades a day of plumbing for permanent loss of integrity. Each section keeps
 * its own endpoint, its own schema and its own guard; this only says the
 * section exists and who may see it.
 *
 * **Availability is a permission question.** There is no feature-flag system
 * here, and inventing one for this would be speculative. A section appears
 * when the caller holds the permissions that govern the thing it configures --
 * which is the same rule the component catalog and every guard already use, so
 * enabling a capability for a role enables its settings in the same motion.
 *
 * **Editors are code, not generated forms.** The component catalog ships JSON
 * Schema over the wire because a dashboard component's configuration form is
 * generated. Settings are edited rarely and read carefully: a working-weekday
 * mask and a holiday list want a purpose-built editor, and a generated form
 * would be a worse version of what already exists. So the catalog carries no
 * schema, and nothing here has to cross the wire.
 */

/**
 * Categories group the navigation, and they are deliberately *not* module keys
 * even though two of them share a name. A category is a heading an
 * administrator reads; a module is something a client bought. They drift apart
 * the moment one module owns settings that belong under two headings, or two
 * modules own settings that belong under one -- and a heading list derived from
 * the module catalog would then be unable to say so. Cheap to unify later if
 * they turn out to stay aligned; expensive to separate again if they do not.
 */
export const SETTINGS_CATEGORIES = [
  'scheduling',
  'spatial',
  'infrastructure',
  'commercial',
  'organization',
] as const;
export const settingsCategorySchema = z.enum(SETTINGS_CATEGORIES);
export type SettingsCategory = (typeof SETTINGS_CATEGORIES)[number];

/**
 * What a section configures, and therefore what it needs before it can render.
 *
 * `organization` sections apply to everything the tenant owns. `project` ones
 * are configured per project, so the settings page passes them the workspace's
 * project -- the one the whole application is about (architecture documentation
 * section 2) -- rather than asking which. Anything narrower than a project is
 * the wrong shape for a settings page and belongs on the record it configures.
 */
export const SETTINGS_SCOPES = ['organization', 'project'] as const;
export const settingsScopeSchema = z.enum(SETTINGS_SCOPES);
export type SettingsScope = (typeof SETTINGS_SCOPES)[number];

export interface SettingsSectionDefinition {
  /** Stable key. Appears in the URL, so renaming one breaks saved links. */
  id: string;
  titleKey: string;
  descriptionKey: string;
  category: SettingsCategory;
  scope: SettingsScope;
  /**
   * Every permission the caller must hold. Governs whether the section is
   * listed at all -- and is an affordance only: the endpoints behind each
   * section re-check, exactly as section 13.2 requires.
   */
  requiredPermissions: readonly Permission[];
  /** Sort order within a category. Ties fall back to the title. */
  order: number;
}

export const SETTINGS_CATALOG = {
  'schedule-calendar': {
    id: 'schedule-calendar',
    titleKey: 'settings.sections.scheduleCalendar.title',
    descriptionKey: 'settings.sections.scheduleCalendar.description',
    category: 'scheduling',
    scope: 'project',
    requiredPermissions: ['schedule.calendar.manage'],
    order: 10,
  },
  'map-basemap': {
    id: 'map-basemap',
    titleKey: 'settings.sections.mapBasemap.title',
    descriptionKey: 'settings.sections.mapBasemap.description',
    category: 'spatial',
    scope: 'organization',
    requiredPermissions: ['map.manage'],
    order: 5,
  },
  'map-defaults': {
    id: 'map-defaults',
    titleKey: 'settings.sections.mapDefaults.title',
    descriptionKey: 'settings.sections.mapDefaults.description',
    category: 'spatial',
    scope: 'project',
    requiredPermissions: ['map.manage'],
    order: 10,
  },
  /*
   * Reference lines (contracts: linear-referencing.ts).
   *
   * A settings section rather than a page of its own, because a line is the
   * frame positions are quoted against rather than something anybody visits: it
   * is created once, edited rarely, and read constantly from everywhere else.
   *
   * Project-scoped, since a route belongs to the undertaking that runs along
   * it, and guarded by `map.manage` for the reason the routes are -- a chainage
   * is a location in a different notation.
   */
  'map-reference-lines': {
    id: 'map-reference-lines',
    titleKey: 'settings.sections.mapReferenceLines.title',
    descriptionKey: 'settings.sections.mapReferenceLines.description',
    category: 'spatial',
    scope: 'project',
    requiredPermissions: ['map.manage'],
    order: 20,
  },
  /*
   * Asset types (contracts: infrastructure.ts).
   *
   * The register's vocabulary: what this organization calls the things it keeps
   * track of. A settings section for the same reason reference lines are one --
   * written once when a programme is set up, read on every screen afterwards.
   *
   * Organization-scoped, because vocabulary that changed between two projects
   * of one organization would make the register unreadable across them, and
   * guarded by `infrastructure.manage` -- the right that already governs the
   * things themselves.
   */
  'infrastructure-types': {
    id: 'infrastructure-types',
    titleKey: 'settings.sections.infrastructureTypes.title',
    descriptionKey: 'settings.sections.infrastructureTypes.description',
    category: 'infrastructure',
    scope: 'organization',
    requiredPermissions: ['infrastructure.manage'],
    order: 10,
  },
  /*
   * Organizations are deliberately *not* here. Every account belongs to one,
   * so they are a tab on the users page, beside the people and the roles
   * (docs/organizations-design.md section 3.6) -- configuration is where a
   * register nobody thinks of as "who is in this deployment" goes to be lost.
   */
  /*
   * Fleet compliance (contracts: fleet.ts).
   *
   * Three fields, and each one is something an administrator decides rather
   * than something an environment variable should. The country picks which row
   * of the target catalog applies; the attestation interval decides when a
   * declaration goes stale; the CPV codes decide what a service contract must
   * quote to be in scope by default.
   *
   * Under `fleet.agreement.manage` rather than a settings-specific right: these
   * settings decide what an agreement *means*, so the person who may write
   * agreements is the person who may set them.
   */
  fleet: {
    id: 'fleet',
    titleKey: 'settings.sections.fleet.title',
    descriptionKey: 'settings.sections.fleet.description',
    category: 'commercial',
    scope: 'organization',
    requiredPermissions: ['fleet.agreement.manage'],
    order: 20,
  },
} as const satisfies Record<string, SettingsSectionDefinition>;

export type SettingsSectionId = keyof typeof SETTINGS_CATALOG;

export const SETTINGS_SECTIONS: readonly SettingsSectionDefinition[] =
  Object.values(SETTINGS_CATALOG);

/**
 * `hasOwn` rather than a plain lookup: this id arrives from the URL, and a
 * bare index would answer `/settings/toString` with a function off
 * `Object.prototype`. The component catalog guards the same way.
 */
export function getSettingsSection(id: string): SettingsSectionDefinition | undefined {
  if (!Object.hasOwn(SETTINGS_CATALOG, id)) return undefined;
  return (SETTINGS_CATALOG as Record<string, SettingsSectionDefinition>)[id];
}

/**
 * The sections this caller may configure, in display order.
 *
 * Sorting here rather than in the page keeps every consumer -- the navigation,
 * a future search, the deep-link resolver -- agreeing on one order.
 */
export function listSettingsSections(
  permissions: readonly Permission[],
): SettingsSectionDefinition[] {
  const held = new Set<string>(permissions);
  return SETTINGS_SECTIONS.filter((section) =>
    section.requiredPermissions.every((permission) => held.has(permission)),
  ).sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
}
